// zr-core/e2ee — end-to-end media encryption primitives for the P2P path.
//
// The transport (CoTURN/SFU/signaling) only ever sees ciphertext: each encoded media
// frame is sealed with AES-256-GCM using a key the app supplies out-of-band, so "your
// calls are between you and the person you're connecting with" holds even if the relay
// is compromised. This module is the PURE core — header framing, IV derivation, and a
// WebCrypto-backed frame cryptor. zr-webrtc wires it into RTCRtpSender/Receiver encoded
// streams (Insertable Streams) where the platform supports them.
//
// Wire format per frame:  [ keyId:1 ][ counter:4 BE ][ AES-GCM(ciphertext||tag) ]
// The 12-byte GCM IV is derived from a per-key 12-byte salt XOR the monotonically
// increasing frame counter, guaranteeing IV uniqueness without sending it on the wire.

export const E2EE_HEADER_BYTES = 5; // keyId(1) + counter(4)

/** Crypto surface used by the cryptor — `globalThis.crypto` in browsers and Node 18+. */
type SubtleLike = {
  subtle: SubtleCrypto;
  getRandomValues<T extends ArrayBufferView>(array: T): T;
};

function getCrypto(): SubtleLike {
  const c = (globalThis as any).crypto;
  if (!c?.subtle) {
    throw new Error('E2EE requires WebCrypto (crypto.subtle), unavailable in this runtime.');
  }
  return c as SubtleLike;
}

/**
 * Cast a typed array / buffer to WebCrypto's BufferSource. TS 5.7 narrowed typed arrays to
 * a generic `Uint8Array<ArrayBufferLike>` which no longer structurally matches `BufferSource`
 * (it rejects the SharedArrayBuffer case); at runtime any Uint8Array is a valid BufferSource.
 */
function bs(buf: Uint8Array | ArrayBuffer): BufferSource {
  return buf as unknown as BufferSource;
}

/**
 * True when the platform can do per-frame E2EE with the mechanism this SDK actually
 * implements: the `createEncodedStreams()` Insertable Streams API (Chrome/Edge) + WebCrypto.
 *
 * We deliberately do NOT return true merely because `RTCRtpScriptTransform` exists (Firefox):
 * the SDK's `E2eeContext` only implements `createEncodedStreams`, so claiming support on
 * Firefox would mark E2EE "active" while NOT actually encrypting — and a Chrome↔Firefox call
 * would then feed encrypted frames to a decoder that can't read them (black video). Reporting
 * support honestly means E2EE only engages where it genuinely works, and both peers agree.
 */
export function isE2eeSupported(): boolean {
  const g = globalThis as any;
  const hasCrypto = !!g.crypto?.subtle;
  const hasInsertable =
    typeof g.RTCRtpSender !== 'undefined' &&
    'createEncodedStreams' in (g.RTCRtpSender.prototype || {});
  return hasCrypto && hasInsertable;
}

/** Pack the 5-byte clear header that prefixes every encrypted frame. */
export function packHeader(keyId: number, counter: number): Uint8Array {
  const h = new Uint8Array(E2EE_HEADER_BYTES);
  h[0] = keyId & 0xff;
  h[1] = (counter >>> 24) & 0xff;
  h[2] = (counter >>> 16) & 0xff;
  h[3] = (counter >>> 8) & 0xff;
  h[4] = counter & 0xff;
  return h;
}

export function unpackHeader(buf: Uint8Array): { keyId: number; counter: number } {
  if (buf.length < E2EE_HEADER_BYTES) throw new Error('E2EE frame too short for header.');
  const counter = ((buf[1] << 24) | (buf[2] << 16) | (buf[3] << 8) | buf[4]) >>> 0;
  return { keyId: buf[0], counter };
}

/** Derive a unique 12-byte GCM IV from the key salt and the frame counter. */
export function deriveIv(salt: Uint8Array, keyId: number, counter: number): Uint8Array {
  const iv = salt.slice(0, 12);
  // Fold keyId+counter into the trailing 8 bytes (big-endian-ish) — uniqueness per (key, counter).
  iv[4] ^= keyId & 0xff;
  iv[8] ^= (counter >>> 24) & 0xff;
  iv[9] ^= (counter >>> 16) & 0xff;
  iv[10] ^= (counter >>> 8) & 0xff;
  iv[11] ^= counter & 0xff;
  return iv;
}

/** Deterministically derive a 12-byte salt from raw key bytes (SHA-256, first 12 bytes). */
export async function deriveSalt(rawKey: Uint8Array): Promise<Uint8Array> {
  const digest = await getCrypto().subtle.digest('SHA-256', bs(rawKey));
  return new Uint8Array(digest).slice(0, 12);
}

/**
 * Normalize a developer-supplied key into 32 raw bytes. Accepts a Uint8Array (used as-is
 * when 32 bytes, else SHA-256-stretched) or a passphrase string (SHA-256-derived). This
 * lets apps pass either a generated 256-bit key or a human shared secret.
 */
export async function importSharedKeyBytes(key: Uint8Array | string): Promise<Uint8Array> {
  const bytes =
    typeof key === 'string' ? new TextEncoder().encode(key) : key;
  if (bytes.length === 32) return bytes;
  const digest = await getCrypto().subtle.digest('SHA-256', bs(bytes));
  return new Uint8Array(digest);
}

/**
 * Derive a distinct 256-bit key for one participant identity from the shared room secret.
 * Each sender MUST encrypt with its OWN identity key: if every peer used the raw shared
 * key, their per-frame counters would collide and reuse the same GCM IV across senders —
 * a catastrophic nonce reuse. Receivers derive the same key from the sender's identity.
 */
export async function deriveIdentityKey(baseKey: Uint8Array, identity: string): Promise<Uint8Array> {
  const id = new TextEncoder().encode(`zr-e2ee|${identity}`);
  const material = new Uint8Array(baseKey.length + id.length);
  material.set(baseKey, 0);
  material.set(id, baseKey.length);
  const digest = await getCrypto().subtle.digest('SHA-256', bs(material));
  return new Uint8Array(digest);
}

/**
 * A per-key AES-GCM frame sealer. Encrypt prefixes the clear header; decrypt reads it back
 * and rederives the IV, so sender and receiver need only share the raw key (never the IVs).
 */
export class FrameCryptor {
  private key!: CryptoKey;
  private salt!: Uint8Array;
  private sendCounter = 0;

  private constructor(public readonly keyId: number) {}

  /** Build a cryptor for `rawKey` (use importSharedKeyBytes to normalize input first). */
  static async create(rawKey: Uint8Array, keyId = 0): Promise<FrameCryptor> {
    const c = new FrameCryptor(keyId);
    const crypto = getCrypto();
    c.key = await crypto.subtle.importKey('raw', bs(rawKey), { name: 'AES-GCM' }, false, [
      'encrypt',
      'decrypt',
    ]);
    c.salt = await deriveSalt(rawKey);
    return c;
  }

  /** Seal one frame → [header][ciphertext]. */
  async encrypt(plaintext: ArrayBuffer): Promise<ArrayBuffer> {
    const counter = this.sendCounter++ >>> 0;
    const header = packHeader(this.keyId, counter);
    const iv = deriveIv(this.salt, this.keyId, counter);
    const ct = new Uint8Array(
      await getCrypto().subtle.encrypt({ name: 'AES-GCM', iv: bs(iv) }, this.key, bs(plaintext)),
    );
    const out = new Uint8Array(header.length + ct.length);
    out.set(header, 0);
    out.set(ct, header.length);
    return out.buffer;
  }

  /** Open one frame produced by encrypt(). Throws if authentication fails (tampered). */
  async decrypt(framed: ArrayBuffer): Promise<ArrayBuffer> {
    const buf = new Uint8Array(framed);
    const { keyId, counter } = unpackHeader(buf);
    if (keyId !== this.keyId) {
      throw new Error(`E2EE keyId mismatch (frame ${keyId} vs cryptor ${this.keyId}).`);
    }
    const iv = deriveIv(this.salt, keyId, counter);
    const ct = buf.subarray(E2EE_HEADER_BYTES);
    return getCrypto().subtle.decrypt({ name: 'AES-GCM', iv: bs(iv) }, this.key, bs(ct));
  }
}
